import { useCallback, useRef, useEffect, useMemo, useState } from "react";
import { useParams, Navigate, useNavigate } from "react-router-dom";
import axios, { AxiosError } from "axios";
import { type Conversation } from "@agentic_conversation/api/conversation";
import { type Agent } from "@agentic_conversation/api/agents";
import {
  useSharedChat,
  SharedChatProvider,
} from "@agentic_conversation/contexts/shared-chat-context";
import { useAdminChat } from "@agentic_system/contexts/admin-chat-context";
import { useUserChat } from "@agentic_conversation/contexts/user-chat-context";
import { ConversationView } from "./components/conversation-view";
import { ContactStatusProvider } from "./components/custom/insiti-link/contact-status-context";
import { MessageInput } from "./components/message-input";
import { AgentBadges, ChatEmptyHeader } from "./components/agent-badges";
import { ChatHeader } from "./components/chat-header";
import { ConversationsDrawer } from "./components/conversations-drawer";
import { UsageLimitModal } from "@agentic_conversation/components/usage-limit-modal";
import { ChatProvider } from "./context";
import { ChatScrollProvider } from "./components/chat-scroll-context";
import { Spinner } from "@ui";
import type { MessageThread } from "./types";
import { ThrottleOverlay } from "./components/throttle-overlay";
import { DailyLimitOverlay } from "./components/daily-limit-overlay";

// Hook to get mode context from either admin or user provider
function useChatMode() {
  const adminContext = useAdminChat();
  const userContext = useUserChat();

  const context = adminContext || userContext;

  if (!context) {
    throw new Error(
      "Chat must be used within AdminChatProvider or UserChatProvider",
    );
  }

  return context;
}

// Inner component that renders only when conversation is loaded
function ChatContent() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { conversation, askQuestion, askAgent, conversations, usageLimitModal, throttle, dailyUsage } =
    useSharedChat();
  const { agents, isAdminMode, user } = useChatMode();

  const container = scrollContainerRef.current;

  // Instant jump to the bottom. Charts/tables call this on render so late-
  // growing content always lands us at the last message.
  const scrollToBottom = useCallback(() => {
    const el = scrollContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  // Auto-scroll to bottom when new messages appear
  useEffect(() => {
    if (!container) return;

    container.scrollTop = container.scrollHeight;
  }, [container, conversation?.messages]);

  const handle402Error = (error: unknown) => {
    const axiosError = error as AxiosError<{
      error: string;
      usage?: any;
      message?: string;
    }>;

    if (axiosError.response?.status === 402) {
      const { error: errorType, usage } = axiosError.response.data;
      if (errorType === "subscription_required") {
        usageLimitModal.open("no_subscription", usage);
      }
    }
  };

  const handleSend = (message: string) => {
    askQuestion.mutate(message, {
      onError: handle402Error,
    });
  };

  const handleAgentClick = (agent: Agent) => {
    askAgent.mutate(agent.id, {
      onError: handle402Error,
    });
  };

  const handleSubscribe = () => {
    // Redirect to pricing page - will be determined by Rails helper on backend
    window.location.href = "/abonnements";
  };

  const handleNewConversation = async () => {
    if (isAdminMode) {
      navigate("/system/agentic/conversations/new");
    } else {
      // Get or create empty conversation
      const { data } = await axios.post(
        "/system/agentic_conversation/conversations.json",
      );
      navigate(`/app/conversations/${data.id}`);
    }
  };

  const handleSelectConversation = (id: number) => {
    if (isAdminMode) {
      navigate(`/system/agentic/conversations/${id}`);
    } else {
      navigate(`/app/conversations/${id}`);
    }
  };

  const threads = useMemo((): MessageThread[] => {
    if (!conversation) return [];

    const userMessages = conversation.messages.filter(
      (m) => m.template_type === "user",
    );

    return userMessages.map((user) => {
      const replies = conversation.messages.filter(
        (m) => m.parent_id === user.id,
      );
      return {
        user,
        synthesizer: replies.find((r) => r.template_type === "synthesizer"),
        tools: replies.filter((r) => r.template_type === "tool"),
      };
    });
  }, [conversation]);

  // For user context, agents are already filtered & capped server-side
  // For admin context, we need to filter client-side
  const MAX_BADGES = 8;
  const availableAgents = useMemo(() => {
    if (!agents) return [];

    // User context: already filtered server-side, cap as safety net
    if (!isAdminMode) return agents.slice(0, MAX_BADGES);

    // Admin context: filter for user-facing questions, then cap
    return agents.filter((a) => a.user_facing_question).slice(0, MAX_BADGES);
  }, [agents, isAdminMode]);

  const isEmptyConversation = threads.length === 0;
  const canCreateNewConversation = !isEmptyConversation;

  // Disable input when any message is processing
  const isProcessing = useMemo(() => {
    if (!conversation) return false;
    return conversation.messages.some(
      (m) => m.status === "progress" || m.status === "pending"
    );
  }, [conversation]);

  return (
    <ChatScrollProvider value={scrollToBottom}>
    <ChatProvider sendUserMessageFromSuggestion={handleSend}>
      <div
        data-testid="chat"
        className="flex flex-col h-full bg-slate-50 min-h-0 relative overflow-hidden"
      >
        {/* LLM Sub-header */}
        <ChatHeader
          title={
            isEmptyConversation
              ? "Recherches"
              : (conversation?.title || conversation?.label || "Recherches")
          }
          onOpenHistory={() => setIsDrawerOpen(true)}
          onNewConversation={handleNewConversation}
          canCreateNew={canCreateNewConversation}
        />

        {/* Content area */}
        <div className="z-[1] relative flex flex-col h-full flex-shrink min-h-0 w-full overflow-hidden">
          <div
            ref={scrollContainerRef}
            className="w-full h-full overflow-y-scroll min-h-0"
          >
            {isEmptyConversation && availableAgents.length > 0 ? (
              /* Centered empty state: Header → Input → Badges */
              <div className="flex flex-col items-center justify-center h-full gap-8 py-6 mt-4">
                <ChatEmptyHeader userName={user?.name} />
                <div className="w-[800px] relative">
                  <MessageInput
                    onSend={handleSend}
                    disabled={askQuestion.isPending || isProcessing}
                    dailyUsage={dailyUsage}
                  />
                  {throttle && <ThrottleOverlay retryAfter={throttle.retryAfter} startedAt={throttle.startedAt} />}
                  {dailyUsage?.is_exceeded && (
                    <DailyLimitOverlay resets_at={dailyUsage.resets_at} onSubscribe={handleSubscribe} />
                  )}
                </div>
                <div className="flex flex-col items-center gap-6 my-4">
                  <p className="text-sm text-slate-600 font-medium">
                    Commencez avec
                  </p>
                  <AgentBadges
                    agents={availableAgents}
                    onAgentClick={handleAgentClick}
                    disabled={askAgent.isPending}
                  />
                </div>
              </div>
            ) : (
              /* Active conversation: Fixed 800px width */
              <>
                <div className="py-6 pb-[154px] flex-shrink w-[800px] mx-auto">
                  <ContactStatusProvider
                    contents={conversation.messages.map((m) => m.content)}
                  >
                    <ConversationView threads={threads} />
                  </ContactStatusProvider>
                </div>

                {/* Fixed bottom input */}
                <div className="absolute bottom-4 w-full flex-shrink-0 h-[110px] flex justify-center">
                  <div className="w-[800px] relative">
                    <MessageInput
                      onSend={handleSend}
                      disabled={askQuestion.isPending || isProcessing}
                      dailyUsage={dailyUsage}
                    />
                    {throttle && <ThrottleOverlay retryAfter={throttle.retryAfter} startedAt={throttle.startedAt} />}
                    {dailyUsage?.is_exceeded && (
                      <DailyLimitOverlay resets_at={dailyUsage.resets_at} onSubscribe={handleSubscribe} />
                    )}
                  </div>
                  <div className="h-4 bg-slate-50"></div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Usage limit overlay — inside content area so header remains accessible */}
        <UsageLimitModal
          isOpen={usageLimitModal.isOpen}
          usageData={usageLimitModal.usageData}
          variant={usageLimitModal.variant}
          onClose={usageLimitModal.close}
          onSubscribe={handleSubscribe}
        />

        {/* Conversations Drawer */}
        <ConversationsDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          conversations={conversations || []}
          currentConversationId={conversation?.id}
          onSelectConversation={handleSelectConversation}
          onNewConversation={handleNewConversation}
        />
      </div>
    </ChatProvider>
    </ChatScrollProvider>
  );
}

// Top-level component that handles fetching and loading states
export function Chat() {
  const { id } = useParams<{ id: string }>();
  const conversationId = id ? Number(id) : null;

  // No conversation ID - redirect to conversations list
  if (!conversationId) {
    return <Navigate to="/system/agentic/conversations" replace />;
  }

  // Wrap with SharedChatProvider
  return (
    <SharedChatProvider conversationId={conversationId}>
      <ChatContentWrapper />
    </SharedChatProvider>
  );
}

// Wrapper that handles loading/error states after providers are set up
function ChatContentWrapper() {
  const { conversation, isLoading } = useSharedChat();

  // Loading state
  if (isLoading || !conversation) {
    return (
      <div
        data-testid="chat"
        className="flex flex-col h-full bg-slate-50 min-h-0 relative"
      >
        <div className="flex-1 flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  // Conversation loaded - render content component
  return <ChatContent />;
}

export default Chat;
